
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUpdateSalesMetrics } from "@/hooks/useUpdateSalesMetrics";
import { LeadSourceType } from "@/types/leadSource";
import { PolicyLineType } from "../types";

interface PolicyDialogProps {
  userId: string;
  onPolicyCreated: () => void;
  onOpenChange: (open: boolean) => void;
}

interface PolicyFormState {
  producer: string;
  producerName: string; // Added producer name field
  policyType: string;
  insuranceCarrier: string;
  policyLine: PolicyLineType;
  policyNumber: string;
  startDate: Date;
  endDate: Date;
  premiumAmount: number;
  status: string;
  leadSource: LeadSourceType;
  policyAcquisitionType: string;
  salesDate: Date;
}

interface PolicyHandlers {
  setProducer: (value: string) => void;
  setProducerName: (value: string) => void; // Added handler for producer name
  setPolicyType: (value: string) => void;
  setInsuranceCarrier: (value: string) => void;
  setPolicyLine: (value: string) => void;
  setPolicyNumber: (value: string) => void;
  setStartDate: (date?: Date) => void;
  setEndDate: (date?: Date) => void;
  setPremiumAmount: (value: number) => void;
  setStatus: (value: string) => void;
  setLeadSource: (value: LeadSourceType) => void;
  setPolicyAcquisitionType: (value: string) => void;
  setSalesDate: (date?: Date) => void;
}

export interface PolicyFormData {
  producerId?: string;
  producerName?: string;
  insuranceType: string;
  insuranceCarrier: string;
  policyLine: string;
  policyNumber: string;
  startDate: string;
  endDate: string;
  premiumAmount: number;
  status: string;
  leadSource: LeadSourceType;
  policyAcquisitionType: string;
  salesDate: string;
}

export const usePolicyDialog = ({
  userId,
  onPolicyCreated,
  onOpenChange,
}: PolicyDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateMetrics } = useUpdateSalesMetrics();
  
  const [formState, setFormState] = useState<PolicyFormState>({
    producer: "",
    producerName: "", // Initialize producer name as empty string
    policyType: "",
    insuranceCarrier: "",
    policyLine: "Personal Lines",
    policyNumber: "",
    startDate: new Date(),
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    premiumAmount: 0,
    status: "active",
    leadSource: "Corporate Website",
    policyAcquisitionType: "New Business",
    salesDate: new Date(),
  });

  const handlers: PolicyHandlers = {
    setProducer: (value) => setFormState((prev) => ({ ...prev, producer: value })),
    setProducerName: (value) => setFormState((prev) => ({ ...prev, producerName: value })),
    setPolicyType: (value) => setFormState((prev) => ({ ...prev, policyType: value })),
    setInsuranceCarrier: (value) => setFormState((prev) => ({ ...prev, insuranceCarrier: value })),
    setPolicyLine: (value) => setFormState((prev) => ({ ...prev, policyLine: value as PolicyLineType })),
    setPolicyNumber: (value) => setFormState((prev) => ({ ...prev, policyNumber: value })),
    setStartDate: (date) => date && setFormState((prev) => ({ ...prev, startDate: date })),
    setEndDate: (date) => date && setFormState((prev) => ({ ...prev, endDate: date })),
    setPremiumAmount: (value) => setFormState((prev) => ({ ...prev, premiumAmount: value })),
    setStatus: (value) => setFormState((prev) => ({ ...prev, status: value })),
    setLeadSource: (value) => setFormState((prev) => ({ ...prev, leadSource: value })),
    setPolicyAcquisitionType: (value) => setFormState((prev) => ({ ...prev, policyAcquisitionType: value })),
    setSalesDate: (date) => date && setFormState((prev) => ({ ...prev, salesDate: date })),
  };

  // Form validation
  const isFormValid = 
    formState.policyNumber.trim() !== "" && 
    formState.insuranceCarrier !== "" && 
    formState.premiumAmount > 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Prepare policy data
      const policyData = {
        user_id: userId,
        producer_id: formState.producer || null,
        producer_name: formState.producerName || null, // Include the producer name
        insurance_type: formState.policyType,
        insurance_carrier: formState.insuranceCarrier,
        policy_line: formState.policyLine,
        policy_number: formState.policyNumber,
        start_date: formState.startDate.toISOString().split('T')[0],
        end_date: formState.endDate.toISOString().split('T')[0],
        premium_amount: formState.premiumAmount,
        status: formState.status,
        lead_source: formState.leadSource,
        policy_acquisition_type: formState.policyAcquisitionType,
        sales_date: formState.salesDate.toISOString().split('T')[0]
      };

      // Insert policy
      const { data: responseData, error } = await supabase
        .from('policies')
        .insert(policyData)
        .select();

      if (error) {
        console.error('Policy creation error:', error);
        
        // Try using the edge function for duplicate policies
        if (error.message.includes('violates unique constraint')) {
          const { data: duplicateData, error: duplicateError } = await supabase.functions.invoke(
            'create-policy-with-duplicates',
            {
              body: policyData
            }
          );
          
          if (duplicateError) {
            console.error('Duplicate policy creation failed:', duplicateError);
            throw duplicateError;
          }
          
          console.log('Duplicate policy created successfully:', duplicateData);
        } else {
          throw error;
        }
      }

      console.log('Policy created successfully:', responseData);
      
      // Update sales metrics right after adding a policy
      await updateMetrics();
      
      // Explicitly trigger sales email notification for all admins, producers, and CSRs
      try {
        // Extract the policy ID safely regardless of response format
        let policyId = null;
        if (responseData) {
          if (Array.isArray(responseData) && responseData.length > 0) {
            policyId = responseData[0].id;
          } else if (typeof responseData === 'object' && 'id' in responseData) {
            policyId = responseData.id;
          }
        }
        
        if (policyId) {
          await supabase.functions.invoke('trigger-sales-email', {
            body: { policyId }
          });
          console.log('Sales email notification triggered successfully');
        } else {
          console.warn('Could not extract policy ID from response, skipping email notification');
        }
      } catch (emailError) {
        console.error('Error triggering sales email:', emailError);
        // Don't fail the whole operation if email fails
      }
      
      onPolicyCreated();
    } catch (err) {
      console.error('Policy submission error:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    dialogOpen: true,
    setDialogOpen: onOpenChange,
    handleSubmit,
    formState,
    handlers,
    isFormValid
  };
};
